<template>
  <div class="order-management-container">
    <el-card class="order-card">
      <!-- 步骤条 -->
      <el-steps v-if="showSteps" :active="activeStep" finish-status="success" process-status="process" align-center>
        <el-step 
          v-for="(step, index) in statusSteps" 
          :key="index"
          :title="step.title"
          :description="step.description"
          class="step-item">
          <template slot="title">
            <div class="step-title-wrapper" @click="handleStepClick(step.status); stepDebugLog(step, index)">
              {{ step.title }}
            </div>
          </template>
          <template slot="description">
            <div v-if="step.time" class="step-time">{{ formatTime(step.time) }}</div>
            <div v-if="step.orderSn" class="step-order">{{ step.orderSn }}</div>
          </template>
        </el-step>
      </el-steps>
      
      <!-- 状态对应的子页面 -->
      <div class="status-content" :class="{ 'no-steps': !showSteps }">
        <div v-loading="loading" element-loading-text="加载中..." class="content-container">
          <component :is="currentComponent" :order-id="$route.params.id"></component>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'OrderManagement',
  data() {
    return {
      activeStep: 0,
      statusSteps: [],
      loading: false,
      // 状态到步骤的映射
      statusStepMap: {
        'CREATE_ORDER': { title: '创建订单', status: 'CREATE_ORDER' },
        'NOT_SHIPPED': { title: '邮寄样品', status: 'NOT_SHIPPED' },
        'IN_TRANSIT': { title: '测试中', status: 'IN_TRANSIT' },
        'TESTING': { title: '测试中', status: 'TESTING' },
        'TESTED': { title: '已测试', status: 'TESTED' },
        'DISPUTE': { title: '争议处理', status: 'DISPUTE' },
        'RETESTING': { title: '复测', status: 'RETESTING' },
        'RETURN': { title: '回寄样品', status: 'RETURN' },
        'FORWARD': { title: '转寄样品', status: 'FORWARD' },
        'UNSETTLED': { title: '结算', status: 'UNSETTLED' },
        'COMPLETED': { title: '已完成', status: 'COMPLETED' },
        'UPLOAD_RESULT': { title: '上传测试结果', status: 'UPLOAD_RESULT' },
        'UPLOAD_RETEST': { title: '上传复测结果', status: 'UPLOAD_RETEST' },
        'UPLOAD_CONTRACT': { title: '上传合同', status: 'UPLOAD_CONTRACT' },
        'UPLOAD_INVOICE': { title: '上传发票', status: 'UPLOAD_INVOICE' },
        'UPLOAD_REPORTS': { title: '上传其他附件', status: 'UPLOAD_REPORTS' },
        'SUBMIT_DISPUTE': { title: '提出争议', status: 'DISPUTE' },
        'SUBMIT_RETEST': { title: '申请复测', status: 'RETESTING' },
        'RESOLVE_DISPUTE': { title: '解决争议', status: 'RESOLVE_DISPUTE' },
        'CREATE_FORWARD': { title: '创建转寄订单', status: 'CREATE_FORWARD' },
        'PROCESS_RETEST': { title: '处理复测', status: 'PROCESS_RETEST' }
      }
    }
  },
  computed: {
    showSteps() {
      // 获取用户角色
      const userRole = localStorage.getItem('userRole')
      
      // 如果是客户端，不显示步骤条
      if (userRole === 'customer') {
        return false
      }
      
      // 在创建订单、创建转寄订单、订单详情和邮寄样品页面显示步骤条
      const path = this.$route.path
      return path.includes('create') || 
             path.includes('detail') || 
             path.includes('management') ||
             path.includes('create-forward')
    },
    currentComponent() {
      // 根据当前状态返回对应的组件
      const currentStatus = this.getCurrentStatus()
      const action = this.$route.query.action
      
      // 如果是邮寄操作，显示邮寄表单
      if (action === 'ship' && currentStatus === 'NOT_SHIPPED') {
        return () => import('./status/NotShippedForm.vue')
      }
      
      // 如果是回寄操作，显示回寄表单
      if (action === 'return' && currentStatus === 'RETURN') {
        return () => import('./status/ReturnShipForm.vue')
      }

      // 如果是转寄操作，显示转寄表单
      if (action === 'transfer' && currentStatus === 'FORWARD') {
        return () => import('./status/ForwardShipForm.vue')
      }
      
      // 如果是提出争议操作，显示争议提交表单
      if ((action === 'dispute' && currentStatus === 'DISPUTE') || 
          currentStatus === 'SUBMIT_DISPUTE') {
        return () => import('./status/DisputeSubmitForm.vue')
      }
      
      // 如果是申请复测操作，显示复测申请表单
      if (currentStatus === 'SUBMIT_RETEST') {
        return () => import('./status/RetestSubmitForm.vue')
      }
      
      // 如果是创建转寄订单操作，显示创建订单表单并传递type='forward'
      if (currentStatus === 'CREATE_FORWARD' || (action === 'create_forward' && currentStatus === 'CREATE_FORWARD')) {
        const CreateOrder = () => import('./CreateOrder.vue')
        return {
          render(h) {
            return h(CreateOrder, { props: { type: 'forward' } })
          }
        }
      }
      
      const componentMap = {
        'CREATE_ORDER': () => import('./CreateOrder.vue'),
        'NOT_SHIPPED': () => import('./status/NotShippedForm.vue'),
        'IN_TRANSIT': () => import('./status/InTransitOrders.vue'),
        'TESTING': () => import('./status/TestingOrders.vue'),
        'TESTED': () => import('./status/TestedOrders.vue'),
        'DISPUTE': () => import('./status/DisputeOrders.vue'),
        'RETESTING': () => import('./status/RetestingOrders.vue'),
        'RETURN': () => import('./status/ReturnShipForm.vue'),
        'FORWARD': () => import('./status/ForwardShipForm.vue'),
        'UNSETTLED': () => import('./status/UnsettledOrders.vue'),
        'COMPLETED': () => import('./status/CompletedOrders.vue'),
        'UPLOAD_RESULT': () => import('./status/UploadTestResult.vue'),
        'UPLOAD_RETEST': () => import('./status/UploadTestResult.vue'),
        'UPLOAD_CONTRACT': () => import('./status/UploadContractForm.vue'),
        'UPLOAD_INVOICE': () => import('./status/UploadInvoiceForm.vue'),
        'UPLOAD_REPORTS': () => import('./reports/UploadReports.vue'),
        'RESOLVE_DISPUTE': () => import('./status/ResolveDisputeForm.vue'),
        'PROCESS_RETEST': () => import('./status/ProcessRetestForm.vue')
      }
      return componentMap[currentStatus] || null
    }
  },
  created() {
    this.initializeSteps()
  },
  watch: {
    // 监听路由变化，重新初始化步骤条
    '$route': {
      handler() {
        this.initializeSteps()
      },
      deep: true
    }
  },
  methods: {
    async initializeSteps() {
      try {
        this.loading = true
        // 检查是否是创建订单页面
        const isCreateOrder = this.$route.path.includes('create')
        const orderId = this.$route.params.id
        const queryStep = this.$route.query.step
        
        if (isCreateOrder || !orderId) {
          // 如果是创建订单页面或没有订单ID，只显示创建订单步骤
          this.statusSteps = [
            { title: '创建订单', status: 'CREATE_ORDER', time: '', orderSn: '', description: '' }
          ]
          this.activeStep = 0
        } else {
          // 获取订单状态日志
          const statusLog = await adminApi.getOrderStatusLog(orderId)
          if (statusLog && statusLog.length > 0) {
            // 转换状态日志为步骤数据
            this.statusSteps = statusLog.map(log => ({
              title: this.statusStepMap[log.statusName]?.title || log.statusName,
              status: log.statusName,
              time: log.createTime,
              orderSn: log.orderSn,
              description: ''
            }))
            
            // 如果有查询参数中的步骤，设置对应的激活步骤
            if (queryStep) {
              const stepIndex = this.statusSteps.findIndex(step => step.status === queryStep)
              if (stepIndex !== -1) {
                this.activeStep = stepIndex
              } else {
                // 如果找不到对应步骤，添加一个新步骤
                const newStep = {
                  title: this.statusStepMap[queryStep]?.title || queryStep,
                  status: queryStep,
                  time: new Date().toISOString(),
                  orderSn: statusLog[0].orderSn,
                  description: ''
                }
                this.statusSteps.push(newStep)
                this.activeStep = this.statusSteps.length - 1
              }
            } else {
              this.activeStep = this.statusSteps.length - 1
            }
          } else {
            // 如果没有状态日志，但有步骤参数，创建初始步骤
            if (queryStep) {
              this.statusSteps = [
                { 
                  title: this.statusStepMap[queryStep]?.title || queryStep,
                  status: queryStep,
                  time: new Date().toISOString(),
                  orderSn: '',
                  description: ''
                }
              ]
              this.activeStep = 0
            }
          }
        }
      } catch (error) {
        console.error('获取订单状态日志失败:', error)
        this.$message.error('获取订单状态日志失败')
      } finally {
        this.loading = false
      }
    },
    getCurrentStatus() {
      // 如果是创建订单页面
      if (this.$route.path.includes('create')) {
        return 'CREATE_ORDER'
      }
      
      // 如果有查询参数中的步骤，优先使用查询参数中的步骤
      const queryStep = this.$route.query.step
      if (queryStep) {
        return queryStep
      }
      
      // 否则返回当前激活步骤的状态
      return this.statusSteps[this.activeStep]?.status || 'CREATE_ORDER'
    },
    handleStepClick(status) {
      // 添加调试信息
      console.log('步骤被点击:', status);
      
      // 设置加载状态
      this.loading = true;
      
      try {
        // 处理状态值可能是中文的情况
        let statusCode = status;
        // 中文状态到代码值的映射
        const statusMapping = {
          '创建订单': 'CREATE_ORDER',
          '邮寄样品': 'NOT_SHIPPED',
          '测试中': 'TESTING',
          '已测试': 'TESTED',
          '争议处理': 'DISPUTE',
          '复测': 'RETESTING',
          '回寄样品': 'RETURN',
          '转寄样品': 'FORWARD',
          '结算': 'UNSETTLED',
          '已完成': 'COMPLETED',
          '上传测试结果': 'UPLOAD_RESULT',
          '上传复测结果': 'UPLOAD_RETEST',
          '上传合同': 'UPLOAD_CONTRACT',
          '上传发票': 'UPLOAD_INVOICE',
          '上传其他附件': 'UPLOAD_REPORTS',
          '提出争议': 'SUBMIT_DISPUTE',
          '申请复测': 'SUBMIT_RETEST',
          '解决争议': 'RESOLVE_DISPUTE',
          '创建转寄订单': 'CREATE_FORWARD',
          '处理复测结果': 'PROCESS_RETEST'
        };
        
        if (statusMapping[status]) {
          statusCode = statusMapping[status];
          console.log('状态值是中文，已转换为代码值:', statusCode);
        }
        
        // 根据状态跳转到对应页面
        const routeMap = {
          'CREATE_ORDER': '/admin/orders/create',
          'NOT_SHIPPED': `/admin/orders/management/${this.$route.params.id}?step=NOT_SHIPPED&action=ship`,
          'IN_TRANSIT': `/admin/orders/management/${this.$route.params.id}?step=IN_TRANSIT`,
          'TESTING': `/admin/orders/management/${this.$route.params.id}?step=TESTING`,
          'TESTED': `/admin/orders/management/${this.$route.params.id}?step=TESTED`,
          'DISPUTE': `/admin/orders/management/${this.$route.params.id}?step=DISPUTE`,
          'RETESTING': `/admin/orders/management/${this.$route.params.id}?step=RETESTING`,
          'RETURN': `/admin/orders/management/${this.$route.params.id}?step=RETURN&action=return`,
          'FORWARD': `/admin/orders/management/${this.$route.params.id}?step=FORWARD&action=transfer`,
          'UNSETTLED': `/admin/orders/management/${this.$route.params.id}?step=UNSETTLED`,
          'COMPLETED': `/admin/orders/management/${this.$route.params.id}?step=COMPLETED`,
          'UPLOAD_RESULT': `/admin/orders/management/${this.$route.params.id}?step=UPLOAD_RESULT&action=upload`,
          'UPLOAD_RETEST': `/admin/orders/management/${this.$route.params.id}?step=UPLOAD_RETEST&action=upload_retest`,
          'UPLOAD_CONTRACT': `/admin/orders/management/${this.$route.params.id}?step=UPLOAD_CONTRACT&action=upload_contract`,
          'UPLOAD_INVOICE': `/admin/orders/management/${this.$route.params.id}?step=UPLOAD_INVOICE&action=upload_invoice`,
          'UPLOAD_REPORTS': `/admin/orders/management/${this.$route.params.id}?step=UPLOAD_REPORTS&action=upload_reports`,
          'SUBMIT_DISPUTE': `/admin/orders/management/${this.$route.params.id}?step=SUBMIT_DISPUTE&action=dispute`,
          'SUBMIT_RETEST': `/admin/orders/management/${this.$route.params.id}?step=SUBMIT_RETEST&action=retest`,
          'RESOLVE_DISPUTE': `/admin/orders/management/${this.$route.params.id}?step=RESOLVE_DISPUTE&action=resolve_dispute`,
          // 使用query参数而不是直接导航到create-forward
          'CREATE_FORWARD': `/admin/orders/management/${this.$route.params.id}?step=CREATE_FORWARD&action=create_forward`,
          'PROCESS_RETEST': `/admin/orders/management/${this.$route.params.id}?step=PROCESS_RETEST&action=process_retest`
        };
        
        const route = routeMap[statusCode];
        console.log('要跳转的路由:', route);
        console.log('当前路由:', this.$route.fullPath);
        
        if (route) {
          // 直接使用this.$router.push进行跳转
          this.$router.push(route).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('路由跳转错误:', err);
              this.$message.error('路由跳转失败');
            }
            this.loading = false;
          }).finally(() => {
            setTimeout(() => {
              this.loading = false;
            }, 500);
          });
        } else {
          // 如果没有对应的路由，直接关闭加载状态
          console.warn('未找到对应的路由:', statusCode);
          this.loading = false;
        }
      } catch (err) {
        console.error('路由处理错误:', err);
        this.loading = false;
      }
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    stepDebugLog(step, index) {
      console.log('步骤调试 - 步骤被点击:', step);
      console.log('步骤调试 - 步骤索引:', index);
      console.log('步骤调试 - 步骤状态:', step.status);
      // 在控制台中警告信息，便于识别
      console.warn('步骤点击事件已触发');
    }
  }
}
</script>

<style scoped>
.order-management-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 20px;
  background-color: #f5f7fa;
}

.order-card {
  width: 100%;
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.el-steps {
  padding: 20px;
  background-color: #fff;
  border-radius: 8px 8px 0 0;
}

.status-content {
  flex: 1;
  padding: 20px;
  background-color: #fff;
  border-radius: 0 0 8px 8px;
  overflow-y: auto;
  min-height: 400px;
  position: relative;
}

.status-content.no-steps {
  border-radius: 8px;
}

.content-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

/* 确保子组件也能占满整个区域 */
.status-content > * {
  width: 100%;
  height: auto;
  min-height: 100%;
}

/* 步骤条样式 */
.el-step {
  cursor: pointer;
  user-select: none;
}

.step-item {
  position: relative;
  transition: all 0.3s;
}

.step-title-wrapper {
  width: 100%;
  text-align: center;
}

.el-step:hover .el-step__title,
.el-step:hover .el-step__description {
  color: #409EFF !important;
}

.el-step:hover .el-step__head {
  transform: scale(1.1);
  transition: transform 0.3s;
}

.step-time {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.step-order {
  font-size: 12px;
  color: #606266;
  margin-top: 3px;
}

/* 确保步骤条标题和描述文字居中对齐 */
.el-step__main {
  text-align: center;
}

.el-step__title {
  font-size: 14px;
  line-height: 1.5;
  margin-top: 8px;
}

.el-step__description {
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
}

/* 活跃步骤的高亮效果 */
.el-steps .is-process .el-step__title,
.el-steps .is-finish .el-step__title {
  font-weight: bold;
}

/* 步骤间连接线动画 */
.el-step__line {
  transition: background-color 0.3s;
}
</style>
